// Path: ./src/app/pages/UploadFlyers/useFlyerTableColumns.tsx
// setTableColumn.tsx

import { GridRenderCellParams } from "@mui/x-data-grid";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { useTranslation } from "react-i18next";
import {
  GfFlyer,
  GfFlyerDto,
  GfFlyerItemList,
  GfFlyerTileJob,
  GfStoreDto,
  AiGenerationStatus,
  JobTestingRecord,
  SettingKey,
} from "@swagger/typescript-fetch-goflyer";
import { useNavigate } from "react-router-dom";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { Button, Chip, IconButton, Tooltip } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import { useMemo } from "react";
import {
  useTableSettingsConfig,
  applyColumnsConfig,
} from "../../../hooks/useTableSettingsConfig";
import { routes } from "../../../utils/constant/navigationRoutes";
import { buildAiGenerationEffortColumn } from "../../components/AiGenerationEffortColumn";
import { hasAiGenerationRunDetails } from "../CreateFlyerItemListPage/AiErrorLogModal";

export const useFlyerTableColumns = (
  handleErrorLogClick?: (itemList: GfFlyerItemList) => void,
  handleGenerateCoordinates?: (flyer: GfFlyer) => void,
  generatingIds: string[] = [],
  handleRetryTileJob?: (flyer: GfFlyer) => void,
  retryingIds: string[] = [],
  handleRetryCoordinates?: (flyer: GfFlyer) => void,
  retryingCoordinateIds: string[] = [],
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { columnsConfig } = useTableSettingsConfig(SettingKey.TableFlyers);
  const values: AllowedTableColumn[] = [
    "validStartDate",
    "validEndDate",
    "updatedAt",
    "createdAt",
  ];
  /**
   * Handles the click event to navigate to the store editing page with pre-filled data.
   *
   * @param value - The EditProps containing store details for editing.
   */
  const handleOnClickEdit = async (value: GfStoreDto) => {
    navigate(routes.uploadFlyer.editById(value?.id || ""));
  };

  const baseColumns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <RenderEditButton
            onClick={handleOnClickEdit}
            {...props}
          ></RenderEditButton>
        );
      },
      sortable: false,
    },

    {
      field: "pdf",
      width: 200,
      headerName: `${t(messages["name"]())}`,
    },
    {
      field: "merchant",
      width: 200,
      headerName: `${t(messages["Merchant"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerDto>) => {
        const store = params.row.stores[0];
        return <div>{makeStoreDisplayName(store)}</div>;
      },
    },
    {
      field: "flyerTag",
      width: 200,
      headerName: `${t(messages["flyerTag"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const flyerTag = params.row.flyerTag;
        return <div>{flyerTag?.map((tag) => tag.name).join(", ")}</div>;
      },
    },
    ...values.map((value) => {
      return {
        width: 200,
        field: value,
        headerName: `${t(messages[value]())}`,
        type: "dateTime",
        valueGetter: (val: GridRenderCellParams) => {
          return val && new Date(val?.row[value]);
        },
      };
    }),
    {
      field: "CreatedBy",
      filterable: false,
      width: 100,
      headerName: `${t(messages["CreatedBy"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const { firstName, lastName, userName, email } =
          params?.row?.adminUser || {};
        const createdBy = firstName
          ? `${firstName} ${lastName || ""}`
          : userName || email || "";

        return <div title={createdBy.trim()}>{createdBy}</div>;
      },
    },
    {
      field: "viewFlyer",
      width: 80,
      headerName: `${t(messages.ViewFlyer())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        return (
          <a
            href={
              process.env.REACT_APP_GOFLYER_WEB_POINT +
              "/flyerview/" +
              (params?.row?.slug !== null ? params?.row?.slug : params?.row?.id)
            }
            rel="noreferrer"
            target="_blank"
          >
            {t(messages.ViewFlyer())}
          </a>
        );
      },
    },
    {
      field: "flyerItemList",
      width: 160,
      headerName: `${t(messages.Flyers_Item_List())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const flyerItemListId = params.row.flyerItemList?.id;
        if (!flyerItemListId) {
          return <div>-</div>;
        }

        return (
          <a
            href={routes.createFlyer.byId(flyerItemListId)}
            rel="noreferrer"
            target="_blank"
            onClick={(event) => event.stopPropagation()}
          >
            {t(messages.Open_In_Item_Editor())}
          </a>
        );
      },
    },
    {
      field: "autoUploadedSource",
      width: 200,
      headerName: `AutoUploadedSource`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const source = params?.row?.autoUploadedSource;
        if (!source) {
          return <div>-</div>;
        }

        try {
          const url = new URL(source);
          return (
            <a href={source} target="_blank" rel="noreferrer">
              {url.hostname}
            </a>
          );
        } catch (error) {
          return <div title={source}>{source}</div>;
        }
      },
    },
    {
      field: "sourceImageCount",
      width: 80,
      headerName: `SourceImageCount`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        return <div>{params?.row?.sourceImageCount}</div>;
      },
    },
    {
      field: "generateCoordinates",
      width: 180,
      headerName: `${t(messages.Generate_Coordinates())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const isGenerating = generatingIds.includes(params.row.id as string);

        return (
          <Button
            variant="contained"
            size="small"
            disabled={!handleGenerateCoordinates}
            onClick={(event) => {
              event.stopPropagation();
              handleGenerateCoordinates &&
                handleGenerateCoordinates(params.row);
            }}
            sx={{
              backgroundColor: isGenerating ? "#9E9E9E" : "#f58220",
              color: "#fff !important",
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "none",
              padding: "6px 16px",
              borderRadius: "6px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: isGenerating ? "#9E9E9E" : "#e8722e",
                color: "#fff !important",
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                transform: isGenerating ? "none" : "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              },
              "&.Mui-disabled": {
                backgroundColor: "#9E9E9E",
                color: "#fff !important",
                opacity: 0.8,
              },
            }}
          >
            {isGenerating
              ? t(messages.Processing())
              : t(messages.Generate_Coordinates())}
          </Button>
        );
      },
    },
    {
      field: "jobTestingRecord",
      width: 200,
      headerName: `${t(messages.Smart_Flyer())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const smartFlyer = params?.row.flyerItemList;
        const aiGenerated =
          params?.row.flyerItemList?.jobTestingRecord ===
          JobTestingRecord.AICOORDINATEGENERATION;

        return smartFlyer ? (
          <Chip
            label={
              aiGenerated
                ? t(messages.AI_Generated())
                : t(messages.Figma_Generated())
            }
            size="small"
            color="info"
            sx={{
              fontSize: "12px",
              height: "22px",
              backgroundColor: "#f58220",
              color: "#fff",
              fontWeight: "500",
            }}
          />
        ) : (
          <></>
        );
      },
    },
    {
      field: "aiGenerationStatus",
      width: 180,
      headerName: "AI Generation Status",
      sortable: true,
      type: "singleSelect",
      valueOptions: [
        { value: AiGenerationStatus.WAITING, label: "Waiting" },
        {
          value: AiGenerationStatus.PROCESSING,
          label: t(messages.Processing()),
        },
        { value: AiGenerationStatus.COMPLETED, label: "Completed" },
        {
          value: AiGenerationStatus.PARTIALLYCOMPLETED,
          label: "Partially Completed",
        },
        { value: AiGenerationStatus.FAILED, label: "Failed" },
      ],
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const status = params.row.flyerItemList?.aiGenerationStatus;
        if (!status) return <div>-</div>;

        const statusColors: Record<
          string,
          "default" | "info" | "success" | "warning" | "error"
        > = {
          [AiGenerationStatus.WAITING]: "default",
          [AiGenerationStatus.PROCESSING]: "info",
          [AiGenerationStatus.COMPLETED]: "success",
          [AiGenerationStatus.PARTIALLYCOMPLETED]: "warning",
          [AiGenerationStatus.FAILED]: "error",
        };

        const statusLabels: Record<string, string> = {
          [AiGenerationStatus.WAITING]: "Waiting",
          [AiGenerationStatus.PROCESSING]: t(messages.Processing()),
          [AiGenerationStatus.COMPLETED]: "Completed",
          [AiGenerationStatus.PARTIALLYCOMPLETED]: "Partially Completed",
          [AiGenerationStatus.FAILED]: "Failed",
        };

        return (
          <Chip
            label={statusLabels[status] || status}
            color={statusColors[status] || "default"}
            size="small"
          />
        );
      },
    },
    {
      field: "aiGenerationModel",
      width: 160,
      headerName: "Model",
      sortable: true,
      filterable: false,
      valueGetter: (params: GridRenderCellParams<GfFlyer>) =>
        params.row.flyerItemList?.aiGenerationModel,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const model = params.row.flyerItemList?.aiGenerationModel;
        return (
          <div title={model ?? undefined}>{model ?? "—"}</div>
        );
      },
    },
    buildAiGenerationEffortColumn<GfFlyer>(
      (row) => row.flyerItemList?.aiGenerationEffort,
    ),
    {
      field: "aiGenerationErrorLog",
      width: 160,
      headerName: "AI Error Log",
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const itemList = params.row.flyerItemList;
        const status = itemList?.aiGenerationStatus;
        if (
          status !== AiGenerationStatus.FAILED &&
          status !== AiGenerationStatus.PARTIALLYCOMPLETED
        ) {
          return <div>-</div>;
        }

        const errorLog = itemList?.aiGenerationErrorLog;
        if (!errorLog && !hasAiGenerationRunDetails(itemList)) {
          return <div>-</div>;
        }

        const hasErrors = errorLog
          ? errorLog.failedImages > 0 || Boolean(errorLog.generalError)
          : Boolean(itemList?.aiGenerationErrorType);

        return (
          <Tooltip
            title={
              errorLog
                ? hasErrors
                  ? `${errorLog.failedImages} failed of ${errorLog.totalImages} total`
                  : "No Errors"
                : "AI Run Details"
            }
          >
            <IconButton
              size="small"
              color={hasErrors ? "error" : "success"}
              onClick={(event) => {
                event.stopPropagation();
                if (handleErrorLogClick && itemList) {
                  handleErrorLogClick(itemList);
                }
              }}
            >
              <ErrorOutlineIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "reviewStatus",
      width: 200,
      headerName: `Review Status`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const smartFlyer = params?.row.flyerItemList?.reviewStatus;

        return smartFlyer ? (
          <Chip
            label={params?.row.flyerItemList?.reviewStatus}
            size="small"
            color="info"
            sx={{
              fontSize: "12px",
              height: "22px",
              backgroundColor: "#f58220",
              color: "#fff",
              fontWeight: "500",
            }}
          />
        ) : (
          <></>
        );
      },
    },

    {
      field: "tileJob",
      width: 200,
      headerName: `${t(messages.Tile_Job_Status())}`,
      sortable: false,
      type: "singleSelect",
      valueOptions: [
        { value: GfFlyerTileJob.StatusEnum.WAITING, label: "Waiting" },
        {
          value: GfFlyerTileJob.StatusEnum.INPROGRESS,
          label: "In Progress",
        },
        { value: GfFlyerTileJob.StatusEnum.COMPLETED, label: "Completed" },
        { value: GfFlyerTileJob.StatusEnum.PARTIAL, label: "Partial" },
        { value: GfFlyerTileJob.StatusEnum.ERROR, label: "Error" },
      ],
      valueGetter: (params: GridRenderCellParams<GfFlyer>) =>
        params.row.tileJob?.status,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const tileJob = params.row.tileJob;
        if (!tileJob) return <div>-</div>;

        const statusColors: Record<
          string,
          "default" | "info" | "success" | "warning" | "error"
        > = {
          [GfFlyerTileJob.StatusEnum.WAITING as unknown as string]: "default",
          [GfFlyerTileJob.StatusEnum.INPROGRESS as unknown as string]: "info",
          [GfFlyerTileJob.StatusEnum.COMPLETED as unknown as string]: "success",
          [GfFlyerTileJob.StatusEnum.PARTIAL as unknown as string]: "warning",
          [GfFlyerTileJob.StatusEnum.ERROR as unknown as string]: "error",
        };

        const label = tileJob.totalTiles
          ? `${tileJob.status} (${tileJob.completedTiles}/${tileJob.totalTiles})`
          : `${tileJob.status}`;

        const tooltipTitle = tileJob.errorMessage
          ? tileJob.errorMessage
          : `Completed: ${tileJob.completedTiles} / Total: ${tileJob.totalTiles ?? "?"}`;

        return (
          <Tooltip title={tooltipTitle}>
            <Chip
              label={label}
              color={
                statusColors[tileJob.status as unknown as string] ?? "default"
              }
              size="small"
            />
          </Tooltip>
        );
      },
    },
    {
      field: "retryTileJob",
      width: 110,
      headerName: `${t(messages.Retry_Tile_Job())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const isRetrying = retryingIds.includes(params.row.id as string);

        return (
          <Tooltip
            title={
              isRetrying
                ? t(messages.Processing())
                : t(messages.Retry_Tile_Job())
            }
          >
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={!handleRetryTileJob || isRetrying}
                onClick={(event) => {
                  event.stopPropagation();
                  if (handleRetryTileJob) {
                    handleRetryTileJob(params.row);
                  }
                }}
              >
                <ReplayIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "retryCoordinates",
      width: 110,
      headerName: `${t(messages.Retry_Coordinates())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyer>) => {
        const status = params.row.flyerItemList?.aiGenerationStatus;
        if (
          status !== AiGenerationStatus.FAILED &&
          status !== AiGenerationStatus.PARTIALLYCOMPLETED
        ) {
          return null;
        }

        const flyerId = params.row.id as string;
        const isRetrying = retryingCoordinateIds.includes(flyerId);

        return (
          <Tooltip
            title={
              isRetrying
                ? t(messages.Processing())
                : t(messages.Retry_Coordinates())
            }
          >
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={!handleRetryCoordinates || isRetrying}
                onClick={(event) => {
                  event.stopPropagation();
                  if (handleRetryCoordinates) {
                    handleRetryCoordinates(params.row);
                  }
                }}
              >
                <ReplayIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "id",
      filterable: false,
      width: 300,
      headerName: `${t(messages["id"]())}`,
    },
  ];

  const columns = useMemo(
    () => applyColumnsConfig(baseColumns, columnsConfig),
    [baseColumns, columnsConfig],
  );

  return columns;
};
